<template>
  <div>
    <div class="p-box">
      <div class="p-box__body">
        <ValidationObserver v-slot="{ handleSubmit }">
          <form v-if="!loading" @submit.prevent="handleSubmit($event, onSubmit)">
            <div class="c-grid">
              <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__12 c-grid__item__l__12">
                <h3>{{ userCompany.attributes.name }}</h3>
                <div>{{ userCompany.attributes.email }}</div>
                <KkRoleSelect
                  v-model:model-value="role"
                  v-model:select-type="selectType"
                  v-model:store-uuids="storeUuids"
                  :is-owner="loginUserIsOwner"
                  @handle-change-check-box="handleChangeCheckBox"
                />
              </div>
            </div>
            <div class="c-button c-button--right">
              <router-link class="c-button__item c-button__item--outline-cancel" :to="{ name: 'CompaniesManagementMembers', params: { company_uuid: companyUuid } }">{{ $t('labels.cancel') }}</router-link>
              <button class="c-button__item c-button__item--primary">
                {{ $t('labels.save') }}
              </button>
            </div>
          </form>
          <KkLoading v-else />
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { roleConst } from '../modules/roleConst'
import ImgIconPlusRed from '../../../images/common/icon_plus_red.svg'
import { KkLoading } from '../components/base'
import { KkRoleSelect } from '../domains/management/components'

export default {
  name: 'CompaniesMemberRoleEdit',
  components: {
    KkLoading,
    KkRoleSelect,
  },
  emits: ['update-flash', 'alert'],
  data() {
    return {
      role: null,
      showActionModal: false,
      storeUuids: [],
      selectType: 'all',
    }
  },
  computed: {
    ...mapState({
      userCompany: state => state.companiesUserCompany.data,
      userCompanyLoading: state => state.companiesUserCompany.loading,
      userCompanyErrors: state => state.companiesStores.errors,
      locationStores: state => state.companiesLocationStores.data,
      locationStoresLoading: state => state.companiesLocationStores.loading,
      locationStoresErrors: state => state.companiesLocationStores.errors,
      invitationsLoading: state => state.companiesInvitations.loading,
      gmbSearchStoresLoading: state => state.companiesGmbSearchStores.loading,
      sites: state => state.companiesSearchTagGroups.data.sites,
      brands: state => state.companiesSearchTagGroups.data.brands,
      franchisees: state => state.companiesSearchTagGroups.data.franchisees,
      areas: state => state.companiesSearchTagGroups.data.areas,
      userCompanies: state => state.companiesSearchTagGroups.data.user_companies,
      prefectures : state => state.companiesSearchTagGroups.data.prefectures,
      originalTagGroupA : state => state.companiesSearchTagGroups.data.original_tag_group_a,
      originalTagGroupB : state => state.companiesSearchTagGroups.data.original_tag_group_b,
      originalTagGroupC : state => state.companiesSearchTagGroups.data.original_tag_group_c,
      originalTagGroupD : state => state.companiesSearchTagGroups.data.original_tag_group_d,
      originalTagGroupE : state => state.companiesSearchTagGroups.data.original_tag_group_e,
      formValues: state => state.companiesSearchTagGroups.formValues,
      searchTagGroupsLoading: state => state.companiesSearchTagGroups.loading,
      accessibleApi: state => state.companiesAccessibleApi.data,
    }),
    loading() {
      return this.userCompanyLoading || this.invitationsLoading || this.gmbSearchStoresLoading || this.searchTagGroupsLoading
    },
    pageErrors() {
      return this.userCompanyErrors || this.locationStoresErrors
    },
    companyUuid() {
      return this.$route.params.company_uuid
    },
    userCompanyId() {
      return this.$route.params.user_company_id
    },
    imgIconPlusRed() {
      return ImgIconPlusRed
    },
    selectedRoleIsOwner() {
      return this.role === roleConst.owner
    },
    loginUserRole() {
      return this.accessibleApi ? this.accessibleApi.meta.role : null
    },
    loginUserIsOwner() {
      return this.loginUserRole === roleConst.owner
    },
    readStoresParams() {
      const {
        brands,
        areas,
        franchisees,
        user_companies,
        prefectures,
        sites,
        original_tag_group_a,
        original_tag_group_b,
        original_tag_group_c,
        original_tag_group_d,
        original_tag_group_e,
      } = this.formValues
      return {
        companyUuid: this.companyUuid,
        readMask: ['uuid', 'name'],
        params: {
          per: 9999,
          search: {
            brand_ids: (brands && brands.length === 0) ? undefined : brands,
            area_ids: areas === 0 ? undefined : areas,
            franchisee_ids: franchisees === 0 ? undefined : franchisees,
            user_company_ids: user_companies === 0 ? undefined : user_companies,
            prefectures: prefectures === 0 ? undefined : prefectures,
            site_types: sites === 0 ? undefined : sites,
            original_tag_group_a_tag_ids: original_tag_group_a,
            original_tag_group_b_tag_ids: original_tag_group_b,
            original_tag_group_c_tag_ids: original_tag_group_c,
            original_tag_group_d_tag_ids: original_tag_group_d,
            original_tag_group_e_tag_ids: original_tag_group_e,
          },
        }
      }
    },
  },
  watch: {
    pageErrors: function (val) {
      this.$emit('update-flash', 'alert', val)
    }
  },
  async created() {
    this.readStoresWithMask({
      companyUuid: this.companyUuid,
      readMask: ['uuid', 'name'],
      params: {
        per: 9999,
      },
    })
    await this.readUserCompany({ company_uuid: this.companyUuid, user_company_id: this.userCompanyId })
    this.role = this.userCompany.attributes.role
    this.storeUuids = this.userCompany.relationships.stores.data.map(store => store.id)
    this.selectType = this.userCompany.attributes.readable_all_store ? 'all' : 'select'
    await this.readSearchTagGroups()
    this.setFormValues(this.formInitialValues())
  },
  methods: {
    ...mapActions({
      updateUserCompany: 'companiesUserCompany/updateUserCompany',
      readUserCompany: 'companiesUserCompany/readUserCompany',
      readStoresWithMask: 'companiesLocationStores/readStoresWithMask',
      readSearchTagGroups: 'companiesSearchTagGroups/readSearchTagGroups',
    }),
    ...mapMutations({
      setFormValues: 'companiesSearchTagGroups/setFormValues',
    }),
    async onSubmit() {
      const payload = {
        user_company: {
          role: this.role,
        }
      }
      if (!this.selectedRoleIsOwner) {
        payload.user_company.readable_all_store = this.selectType === 'all'
        if (this.selectType !== 'all') payload.store_uuids = this.storeUuids
      }
      await this.updateUserCompany({ company_uuid: this.companyUuid, user_company_id: this.userCompanyId, payload: payload })
      if (!this.errors) {
        this.$router.push({ name: 'CompaniesManagementMembers', params: { company_uuid: this.companyUuid } })
      }
    },
    formInitialValues() {
      return {
        sites: this.sites.tags.map(v => v.id),
        brands: this.brands.tags.map(v => v.id),
        franchisees: this.franchisees.tags.map(v => v.id),
        areas: this.areas.tags.map(v => v.id),
        user_companies: this.userCompanies.tags.map(v => v.id),
        prefectures: this.prefectures.tags.map(v => v.id),
        original_tag_group_a: this.originalTagGroupA?.tags.map(v => v.id),
        original_tag_group_b: this.originalTagGroupB?.tags.map(v => v.id),
        original_tag_group_c: this.originalTagGroupC?.tags.map(v => v.id),
        original_tag_group_d: this.originalTagGroupD?.tags.map(v => v.id),
        original_tag_group_e: this.originalTagGroupE?.tags.map(v => v.id)
      }
    },
    handleChangeCheckBox() {
      this.readStoresWithMask(this.readStoresParams)
    },
  },
}
</script>
<style lang="scss" scoped>
.p-box {
  margin-top: 0;
}
/* stylelint-disable selector-pseudo-class-no-unknown */
:deep(.radio) {
  display: grid;
}
:deep(.radio__item) {
  margin-bottom: 15px;
}
/* stylelint-enable */
</style>
